import React, { useEffect, useRef, useState } from "react";
import { contentApi } from "./contentApi";
import ContentConfirm from "./ContentConfirm";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

const emptyForm = () => ({
  level: "1",
  theme: "",
  title: "",
  description: "",
  display_order: "",
  active: true,
});
const levels = [1, 2, 3];

export default function TaskEditor({ onMessage, onDirtyChange, onBusyChange }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const [orderDirtyLevels, setOrderDirtyLevels] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const [reconcilePending, setReconcilePending] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [pendingFormDiscard, setPendingFormDiscard] = useState(null);
  const [total, setTotal] = useState(0);
  const { query, update: updateQuery, reset: resetQuery } = useAdminListQuery();
  const generation = useRef(0);
  const busyRef = useRef(false);
  const abortRef = useRef(null);

  const orderDirty = orderDirtyLevels.size > 0;
  const dirty = formDirty || orderDirty;
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(
    () => onBusyChange(busy || reconcilePending),
    [busy, reconcilePending, onBusyChange],
  );

  async function loadRows({ reconcile = false, discardLevel = null } = {}) {
    const current = ++generation.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    if (reconcile) setReconcilePending(true);
    else setReconcilePending(false);
    setError("");
    try {
      const params = new URLSearchParams({ paged: "true", limit: "100" });
      for (const key of ["q", "level", "status", "offset"]) if (query[key]) params.set(key, query[key]);
      const result = await contentApi(`/admin/content/tasks?${params}`, {
        signal: controller.signal,
      });
      if (current !== generation.current) return;
      setRows((currentRows) => {
        const preserveLevels = new Set(orderDirtyLevels);
        if (discardLevel !== null) preserveLevels.delete(discardLevel);
        return [1, 2, 3].flatMap((level) =>
          preserveLevels.has(level)
            ? currentRows.filter((row) => row.level === level)
            : (result.items || []).filter((row) => row.level === level),
        );
      });
      setTotal(Number(result.total) || 0);
      if (discardLevel !== null) {
        setOrderDirtyLevels((current) => {
          const next = new Set(current);
          next.delete(discardLevel);
          return next;
        });
      }
      setRefreshError("");
      setReconcilePending(false);
    } catch (e) {
      if (e.name === "AbortError" || current !== generation.current) return;
      setRefreshError(e.message);
      setReconcilePending(reconcile);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
    return () => {
      generation.current += 1;
      abortRef.current?.abort();
    };
  }, [query.q, query.level, query.status, query.offset]);

  function update(key, value) {
    setForm((old) => ({ ...old, [key]: value }));
    setFormDirty(true);
  }

  function resetForm() {
    setForm(emptyForm());
    setEditing(null);
    setFormDirty(false);
  }

  function edit(row) {
    setEditing(row);
    setForm({
      level: String(row.level),
      theme: row.theme || "",
      title: row.title,
      description: row.description || "",
      display_order: row.display_order ?? "",
      active: row.active !== false,
    });
    setFormDirty(false);
  }

  function requestEdit(row) {
    if (formDirty) setPendingFormDiscard({ row });
    else edit(row);
  }

  function requestReset() {
    if (formDirty) setPendingFormDiscard({ reset: true });
    else resetForm();
  }

  function confirmFormDiscard() {
    const pending = pendingFormDiscard;
    setPendingFormDiscard(null);
    if (pending?.reset) resetForm();
    else if (pending?.row) edit(pending.row);
  }

  function move(level, index, direction) {
    if (busyRef.current) return;
    setRows((old) => {
      const group = old.filter((row) => row.level === level && row.active);
      const target = group[index];
      const other = group[index + direction];
      if (!target || !other) return old;
      const next = [...old];
      const a = next.findIndex((row) => row.id === target.id);
      const b = next.findIndex((row) => row.id === other.id);
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
    setOrderDirtyLevels((current) => new Set(current).add(level));
  }

  async function saveOrder(level) {
    if (busyRef.current || !reorderAllowed) return;
    const ids = rows
      .filter((row) => row.level === level && row.active)
      .map((row) => row.id);
    busyRef.current = true;
    setBusy(true);
    setError("");
    const current = generation.current;
    try {
      const result = await contentApi("/admin/content/tasks/reorder", {
        method: "POST",
        body: { level, ids },
      });
      if (current !== generation.current) return;
      setRows((old) =>
        [1, 2, 3].flatMap((currentLevel) =>
          currentLevel === level
            ? result
            : old.filter((row) => row.level === currentLevel),
        ),
      );
      setOrderDirtyLevels((levelsForOrder) => {
        const next = new Set(levelsForOrder);
        next.delete(level);
        return next;
      });
      onMessage(`Level ${level} order saved`);
    } catch (e) {
      if (current === generation.current)
        setError(
          `${e.message} Your order is still shown. Reload before retrying.`,
        );
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  function discardOrder(level) {
    if (busyRef.current || !orderDirtyLevels.has(level)) return;
    loadRows({ discardLevel: level });
  }

  async function saveForm(event) {
    event.preventDefault();
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    const current = generation.current;
    const editingId = editing?.id;
    const level = Number(form.level);
    try {
      const row = editingId
        ? await contentApi(`/admin/content/tasks/${editingId}`, {
            method: "PATCH",
            body: {
              theme: form.theme,
              title: form.title,
              description: form.description,
              display_order:
                form.display_order === ""
                  ? undefined
                  : Number(form.display_order),
            },
          })
        : await contentApi("/admin/content/tasks", {
            method: "POST",
            body: {
              theme: form.theme,
              title: form.title,
              description: form.description,
              level,
              display_order:
                form.display_order === "" ? 0 : Number(form.display_order),
              active: form.active,
            },
          });
      if (current !== generation.current) return;
      setRows((old) =>
        editingId
          ? old.map((item) => (item.id === row.id ? row : item))
          : [row, ...old],
      );
      resetForm();
      onMessage(editingId ? "Task updated" : "Task created");
      await loadRows({ reconcile: true });
    } catch (e) {
      if (current === generation.current) setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function runAction() {
    const { row, action } = confirm || {};
    setConfirm(null);
    if (!row || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    const current = generation.current;
    try {
      const result =
        action === "delete"
          ? await contentApi(`/admin/content/tasks/${row.id}`, {
              method: "DELETE",
            })
          : await contentApi(`/admin/content/tasks/${row.id}/${action}`, {
              method: "POST",
              body: {},
            });
      if (current !== generation.current) return;
      if (action === "delete")
        setRows((old) => old.filter((item) => item.id !== row.id));
      else
        setRows((old) =>
          old.map((item) =>
            item.id === row.id
              ? result?.id
                ? result
                : { ...item, active: action === "activate" }
              : item,
          ),
        );
      onMessage(`Task ${action}d`);
      await loadRows({ reconcile: true });
    } catch (e) {
      if (current === generation.current) setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  function group(level, active) {
    return rows.filter((row) => row.level === level && row.active === active);
  }
  const reorderAllowed = !query.q && !query.level && !query.status && total <= rows.length;

  return (
    <section
      className="content-editor-section"
      aria-labelledby="task-editor-title"
    >
      <div className="content-section-heading">
        <div>
          <h2 id="task-editor-title">Task templates</h2>
          <p>
            Create and edit task templates, then save the complete active set
            for each level.
          </p>
        </div>
        <button
          type="button"
          className="content-button secondary"
          onClick={() => loadRows({ reconcile: reconcilePending })}
          disabled={busy || loading || reconcilePending}
        >
          Refresh
        </button>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search task title, description, or key" total={total} loading={loading} onSearch={(q) => updateQuery({ q })} onReset={resetQuery}>
        <label className="text-sm font-medium">Level<select aria-label="Filter task level" value={query.level || "all"} onChange={(event) => updateQuery({ level: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All levels</option><option value="1">Level 1</option><option value="2">Level 2</option><option value="3">Level 3</option></select></label>
        <label className="text-sm font-medium">Status<select aria-label="Filter task status" value={query.status || "all"} onChange={(event) => updateQuery({ status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
      </AdminListToolbar>
      {!reorderAllowed && rows.length > 0 && <p className="content-muted" role="status">Ordering controls are available only on the complete unfiltered task list.</p>}
      {error && (
        <p className="content-error" role="alert">
          {error}
        </p>
      )}
      {refreshError && (
        <p className="content-error" role="alert">
          {refreshError}{" "}
          <button
            type="button"
            className="content-button secondary"
            onClick={() => loadRows({ reconcile: true })}
          >
            Retry reload
          </button>
        </p>
      )}
      <form className="content-card content-form" onSubmit={saveForm}>
        <fieldset disabled={busy || loading || reconcilePending}>
          <legend>
            {editing ? "Edit task template" : "Add task template"}
          </legend>
          <div className="content-form-grid">
            <label>
              Level
              <select
                value={form.level}
                disabled={Boolean(editing)}
                onChange={(e) => update("level", e.target.value)}
              >
                <option value="1">Level 1</option>
                <option value="2">Level 2</option>
                <option value="3">Level 3</option>
              </select>
            </label>
            <label>
              Theme
              <input
                required
                maxLength={200}
                value={form.theme}
                onChange={(e) => update("theme", e.target.value)}
              />
            </label>
            <label>
              Title
              <input
                required
                maxLength={300}
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
              />
            </label>
            <label>
              Display order
              <input
                type="number"
                min="0"
                max="10000"
                value={form.display_order}
                onChange={(e) => update("display_order", e.target.value)}
              />
            </label>
          </div>
          <label>
            Description
            <textarea
              required
              maxLength={5000}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
            />
          </label>
          <div className="content-actions">
            <button
              className="content-button primary"
              disabled={busy || loading}
            >
              {busy ? "Saving…" : editing ? "Save changes" : "Add task"}
            </button>
            {editing && (
              <button
                type="button"
                className="content-button secondary"
                onClick={requestReset}
                disabled={busy || reconcilePending}
              >
                Cancel
              </button>
            )}
          </div>
        </fieldset>
      </form>
      {levels.map((level) => (
        <React.Fragment key={level}>
          <div className="content-card">
            <div className="content-section-heading">
              <h3>Level {level} active tasks</h3>
              <button
                type="button"
                className="content-button primary"
                onClick={() => saveOrder(level)}
                disabled={busy || reconcilePending || !reorderAllowed || !orderDirtyLevels.has(level)}
              >
                {busy ? "Saving…" : "Save level order"}
              </button>
              {orderDirtyLevels.has(level) && (
                <button
                  type="button"
                  className="content-button secondary"
                  onClick={() => discardOrder(level)}
                  disabled={busy || loading || reconcilePending}
                >
                  Discard order
                </button>
              )}
            </div>
            {group(level, true).length === 0 ? (
              <p className="content-muted">No active task templates.</p>
            ) : (
              <ol className="content-sort-list">
                {group(level, true).map((row, index, active) => (
                  <li key={row.id}>
                    <span className="content-sort-number">{index + 1}</span>
                    <span className="content-row-main">
                      <strong>{row.title}</strong>
                      <span>{row.theme}</span>
                    </span>
                    <button
                      type="button"
                      className="content-button icon"
                      onClick={() => move(level, index, -1)}
                      disabled={busy || reconcilePending || !reorderAllowed || index === 0}
                      aria-label={`Move ${row.title} up`}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="content-button icon"
                      onClick={() => move(level, index, 1)}
                      disabled={
                        busy || reconcilePending || !reorderAllowed || index === active.length - 1
                      }
                      aria-label={`Move ${row.title} down`}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="content-button secondary"
                      onClick={() => requestEdit(row)}
                      disabled={busy || reconcilePending}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="content-button danger"
                      onClick={() => setConfirm({ row, action: "retire" })}
                      disabled={busy || reconcilePending}
                    >
                      Retire
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <div className="content-card content-inactive-card">
            <h3>Level {level} inactive tasks</h3>
            {group(level, false).length === 0 ? (
              <p className="content-muted">No inactive task templates.</p>
            ) : (
              <div className="content-row-list">
                {group(level, false).map((row) => (
                  <article className="content-row inactive" key={row.id}>
                    <div className="content-row-main">
                      <strong>{row.title}</strong>
                      <span>{row.theme}</span>
                    </div>
                    <div className="content-actions">
                      <button
                        type="button"
                        className="content-button secondary"
                        onClick={() => requestEdit(row)}
                        disabled={busy || reconcilePending}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="content-button primary"
                        onClick={() => setConfirm({ row, action: "activate" })}
                        disabled={busy || reconcilePending}
                      >
                        Activate
                      </button>
                      <button
                        type="button"
                        className="content-button danger"
                        onClick={() => setConfirm({ row, action: "delete" })}
                        disabled={busy || reconcilePending}
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
      <AdminListPager offset={Number(query.offset || 0)} limit={100} total={total} disabled={loading || Boolean(busy)} onPage={(offset) => updateQuery({ offset }, { resetOffset: false })} />
      <ContentConfirm
        open={Boolean(confirm)}
        title={`${confirm?.action === "delete" ? "Delete" : confirm?.action === "retire" ? "Retire" : "Activate"} this task?`}
        description={
          confirm?.action === "delete"
            ? "This permanently removes the task template."
            : "The task state will be changed for future sessions."
        }
        action={
          confirm?.action === "delete"
            ? "Delete"
            : confirm?.action === "retire"
              ? "Retire"
              : "Activate"
        }
        onConfirm={runAction}
        onClose={() => setConfirm(null)}
      />
      <ContentConfirm
        open={Boolean(pendingFormDiscard)}
        title="Discard task changes?"
        description="Opening another task or cancelling will discard the current form."
        action="Discard changes"
        onConfirm={confirmFormDiscard}
        onClose={() => setPendingFormDiscard(null)}
      />
    </section>
  );
}
